import { FEATURE_FLAGS, type ApiV1, type ApiV2 } from '@teamworksdev/influencer/core';
import {
	type ApiClient,
	type FetchClient,
	type RequestMeta,
	Influencer,
} from '@teamworksdev/influencer/sdk';
import { useAuthStore } from '@/stores/authStore';
import { useUserStore } from '@/stores/userStore';
import { isNil } from 'lodash-es';
import type { Plugin } from 'vue';
import { injectionKeys } from '@/utils/injectionKeys';
import { isAfter } from 'date-fns';
import { adaptMinimalUserProfile, adaptUser, adaptUserProfile } from '@/utils/users';
import { handleError } from '@/utils/errorHandling';
import { getClient } from '@/plugins/launchdarkly';
import { isAxiosError } from 'axios';

declare module '@teamworksdev/influencer/sdk' {
	interface RequestMeta {
		/**
		 * If true, while impersonating, this request should use the auth token for the impersonator
		 * and not that of the user being impersonated
		 *
		 * @defaultvalue false
		 */
		bypassImpersonation?: boolean;
	}
}

const newUserLoginFlowEnabled = async (): Promise<boolean> => {
	const ldClient = await getClient();
	return ldClient.variation(FEATURE_FLAGS.NEW_USER_LOGIN_FLOW, false);
};

export class InfluencerWeb {
	#client: Influencer;
	#profiles: Map<string, ApiV1.UserProfile> = new Map();
	#impersonatorProfiles: Map<string, ApiV1.UserProfile> = new Map();

	api: ApiClient;

	constructor() {
		this.#client = new Influencer({
			production: import.meta.env.MODE.startsWith('production'),
			serverUrl: import.meta.env.VITE_API_BASE,
			fetch: this.createCustomFetch(),
			options: {
				quiet: import.meta.env.MODE === 'test',
			},
		});
		this.api = this.#client.api;
	}

	private log(message: string, data?: any) {
		if (import.meta.env.MODE.startsWith('production') || import.meta.env.MODE === 'test') return;
		console.debug(['[InfluencerWeb] ', message, data && ':'].join(''), data);
	}

	private getAuthToken(bypassImpersonation?: boolean): string | null {
		// Right now, the authStore is the source of truth
		// with regard to user authentication during migration.
		//
		// Once we're fully migrated to the Influencer SDK,
		// we will rely on that as our source of truth and
		// deprecate the need for the authStore.
		const authStore = useAuthStore();
		let token = authStore.token;
		let impersonationToken = authStore.impersonationToken;

		if (!isNil(token) && isAfter(new Date(), new Date(token.expires_at))) {
			token = null;
		}

		if (bypassImpersonation) {
			return token?.access_token ?? null;
		}

		if (!isNil(impersonationToken) && isAfter(new Date(), new Date(impersonationToken.expires_at))) {
			impersonationToken = null;
		}

		return impersonationToken?.access_token ?? token?.access_token ?? null;
	}

	private getProfileId(bypassImpersonation?: boolean): string | null {
		const userStore = useUserStore();
		if (bypassImpersonation) {
			return userStore.inflcrImpersonatedById ?? null;
		}
		return userStore.inflcrId ?? null;
	}

	private hasUserAcceptedTou(): boolean {
		const userStore = useUserStore();
		return userStore.hasUserAcceptedLatestTermsOfUse;
	}

	private defaultRequestMeta: Readonly<RequestMeta> = {
		bypassImpersonation: false,
	};

	private createCustomFetch(): FetchClient {
		return async (url: string, init?: RequestInit, meta: RequestMeta = {}): Promise<Response> => {
			meta = {
				...this.defaultRequestMeta,
				...meta,
			};

			this.log(`Request initiated (${meta.bypassImpersonation ? 'delegate' : 'principal'})`, { url, init, meta });

			// Check terms of use if required
			if (meta.requiresUser && meta.requiresTou && !this.hasUserAcceptedTou()) {
				throw new Error('User has not accepted Terms of Use');
			}

			// Build headers
			const headers = new Headers(init?.headers);

			// Add auth header if required
			if (meta.requiresUser) {
				const token = this.getAuthToken(meta.bypassImpersonation);
				if (isNil(token)) {
					const authStore = useAuthStore();
					authStore.clearClientToken();
					throw new Error('Unauthenticated');
				} else {
					headers.set('Authorization', `Bearer ${token}`);
				}
			}

			// Add profile header if required
			if (meta.requiresProfile) {
				const profileId = this.getProfileId(meta.bypassImpersonation);
				if (isNil(profileId)) {
					throw new Error('No active profile available');
				} else {
					headers.set('Inflcr-Id', profileId);
				}
			}

			// Make the request
			return await fetch(url, {
				...init,
				headers,
			});
		};
	}

	async logIn(payload: ApiV1.BasicAuthRequest) {
		const token = await this.api.auth.login(payload);

		const authStore = useAuthStore();
		authStore.setToken(token, payload.remember_me);
	}

	async logOut() {
		if (this.api.getInflcrToken()) {
			await this.api.auth.logout({ bypassImpersonation: true });
		}

		this.#profiles.clear();
		this.#impersonatorProfiles.clear();
	}

	async getUser(options: {
		bypassImpersonation?: boolean;
	} = {
		bypassImpersonation: false,
	}): Promise<ApiV2.User> {
		const { bypassImpersonation } = options;
		const flagEnabled = await newUserLoginFlowEnabled();

		// If we're using the original method to get a user:
		// Populate the profiles map with the original
		// profiles, then return the adapted user.
		if (!flagEnabled) {
			const { data } = await this.api.users.getMe(undefined, { bypassImpersonation });
			if (bypassImpersonation) {
				data.user_profiles.forEach((profile) => {
					this.#impersonatorProfiles.set(profile.uuid, profile);
				});
			} else {
				data.user_profiles.forEach((profile) => {
					this.#profiles.set(profile.uuid, profile);
				});
			}
			return adaptUser(data);
		}

		return await this.api.users.getMe({ version: 2 }, { bypassImpersonation });
	}

	async getMinimalUserProfiles(options: {
		bypassImpersonation?: boolean;
	} = {
		bypassImpersonation: false,
	}) {
		const { bypassImpersonation } = options;
		const flagEnabled = await newUserLoginFlowEnabled();

		if (!flagEnabled) {
			const profiles = bypassImpersonation
				? this.#impersonatorProfiles
				: this.#profiles;
			return Array.from(profiles.values()).map(adaptMinimalUserProfile);
		}

		return await this.api.users.getProfiles({ bypassImpersonation });
	}

	async getFullUserProfile(uuid: string, options: {
		bypassImpersonation?: boolean;
	} = {
		bypassImpersonation: false,
	}): Promise<ApiV1.FullUserProfile> {
		const { bypassImpersonation } = options;
		const flagEnabled = await newUserLoginFlowEnabled();

		// If we're using the original method to get a user:
		// Get the profile from the local cache of profiles
		// and adapt it to the new form.
		if (!flagEnabled) {
			let profile: ApiV1.UserProfile | undefined;
			if (bypassImpersonation) {
				profile = this.#impersonatorProfiles.get(uuid);
			} else {
				profile = this.#profiles.get(uuid);
			}
			if (!profile) throw new Error('Profile not found');
			return adaptUserProfile(profile);
		}

		return await this.api.users.getProfile(uuid, { bypassImpersonation });
	}

	async getDefaultProfile(options: {
		bypassImpersonation?: boolean;
	} = {
		bypassImpersonation: false,
	}): Promise<ApiV1.FullUserProfile | null> {
		const { bypassImpersonation } = options;

		try {
			return await this.api.users.getDefaultProfile({ bypassImpersonation });
		} catch (error) {
			// 404 is expected when user has no default profile
			if (isAxiosError(error) && error.response?.status === 404) {
				this.log('No default profile found (404)', { bypassImpersonation });
				return null;
			}
			// Log other errors but don't throw - allow fallback
			console.warn('[InfluencerWeb] Error fetching default profile:', error);
			return null;
		}
	}

	async setDefaultProfile(uuid: string) {
		try {
			await this.api.users.setDefaultProfile(uuid);
			this.#profiles?.forEach((profile) => {
				profile.is_default = profile.uuid === uuid;
			});
		} catch (error) {
			handleError(error);
		}
	}
}

let inflcr: InfluencerWeb;

export function getInfluencer() {
	if (!inflcr) {
		inflcr = new InfluencerWeb();
	}
	return inflcr;
}

export const InfluencerWebPlugin: Plugin = {
	install: (app) => {
		app.provide(injectionKeys.INFLUENCER, getInfluencer());
	},
};
